import { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useLanguage } from "@/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t('common.messageSent'));
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error(t('common.messageFailed'));
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: MapPin, title: t('contact.address'), content: "123 Rue Sainte-Catherine\nMontréal, QC H3B 1A7" },
    { icon: Phone, title: t('contact.phone'), content: "(438) 342-2030" },
    { icon: Mail, title: t('contact.email'), content: "contact@dreamhousecanada.org" },
    { icon: Clock, title: t('contact.hours'), content: t('contact.hoursValue') }
  ];

  return (
    <div className="pt-36 pb-20 min-h-screen bg-bone" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl md:text-5xl font-medium text-charcoal mb-4">{t('contact.title')}</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">{t('contact.subtitle')}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-soft">
              <h2 className="font-heading text-xl font-medium text-charcoal mb-6">{t('contact.getInTouch')}</h2>
              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <div key={`contact-${item.title}`} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-charcoal">{item.title}</p>
                      <p className="text-sm text-muted-foreground whitespace-pre-line">{item.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-8 shadow-soft">
              <h2 className="font-heading text-xl font-medium text-charcoal mb-6">{t('contact.sendMessage')}</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Input placeholder={t('contact.yourName')} className="rounded-xl" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} required />
                  <Input type="email" placeholder={t('contact.yourEmail')} className="rounded-xl" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} required />
                </div>
                <Input type="tel" placeholder={t('contact.yourPhone')} className="rounded-xl" value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} />
                <Textarea placeholder={t('contact.yourMessage')} className="rounded-xl min-h-[150px]" value={formData.message} onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))} required />
                <Button type="submit" className="w-full md:w-auto rounded-full px-8" disabled={loading}>
                  {loading ? t('contact.sending') : t('contact.send')}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
